import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack,
  FormControlLabel,
  Switch
} from '@mui/material';
import { CheckBox as CheckBoxIcon } from '@mui/icons-material';

// Import API service
import { inspectionTypesApi } from '../../services/api';

const InspectionTypeDialog = ({ 
  open, 
  onClose, 
  mode, 
  assetTypeId, 
  inspectionType, 
  userData, 
  onUpdate 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cycle_frequency: '',
    is_mandatory: false
  });
  const [frequencies, setFrequencies] = useState([]);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && inspectionType) {
        setFormData({
          name: inspectionType.name || '',
          description: inspectionType.description || '',
          cycle_frequency: inspectionType.cycle_frequency || '',
          is_mandatory: inspectionType.is_mandatory || false
        });
      } else {
        setFormData({
          name: '',
          description: '',
          cycle_frequency: '',
          is_mandatory: false
        });
      }
      setError(null);
    }
  }, [open, mode, inspectionType]);

  useEffect(() => {
    if (open) {
      const fetchFrequencies = async () => {
        try {
          const response = await inspectionTypesApi.getFrequencies();
          setFrequencies(response.data || []);
        } catch (err) {
          console.error("Failed to fetch inspection frequencies:", err);
        }
      };
      fetchFrequencies();
    }
  }, [open]);

  const handleChange = (field) => (event) => {
    const value = field === 'is_mandatory' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Inspection type name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        asset_type_id: assetTypeId,
        organization_id: userData.organizationId
      };

      if (mode === 'create') {
        await inspectionTypesApi.createType(submitData);
      } else {
        await inspectionTypesApi.updateType(inspectionType.id, submitData);
      }

      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error saving inspection type:', err);
      setError(err.response?.data?.error || 'Failed to save inspection type');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.name.trim().length > 0;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'info.main', color: 'info.contrastText', display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckBoxIcon />
        {mode === 'create' ? 'Create' : 'Edit'} Inspection Type
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
            
            <TextField
              fullWidth
              label="Inspection Type Name"
              value={formData.name}
              onChange={handleChange('name')}
              required
              placeholder="e.g., Visual Inspection, Structural Assessment"
              helperText="Enter a descriptive name for this inspection type"
              disabled={loading}
            />
          </Box>

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={3}
            placeholder="Describe what this inspection covers and its purpose..."
            helperText="Optional: Provide details about this inspection type"
            disabled={loading}
          />

          <FormControl fullWidth>
            <InputLabel>Cycle Frequency</InputLabel>
            <Select
              value={formData.cycle_frequency}
              onChange={handleChange('cycle_frequency')}
              label="Cycle Frequency"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Not Specified</em>
              </MenuItem>
              {frequencies.map((freq) => (
                <MenuItem key={freq} value={freq}>
                  {freq.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Inspection Settings
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_mandatory}
                  onChange={handleChange('is_mandatory')}
                  color="primary"
                  disabled={loading}
                />
              }
              label="Mandatory Inspection"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Mandatory inspections are required and cannot be skipped
            </Typography>
          </Box>

          {/* Preview information */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Name:</strong> {formData.name || 'Not specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Frequency:</strong> {formData.cycle_frequency || 'Not specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Type:</strong> {formData.is_mandatory ? 'Mandatory' : 'Optional'}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!isFormValid() || loading}
          color="info"
          sx={{ px: 3 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            mode === 'create' ? 'Create Inspection Type' : 'Update Inspection Type'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InspectionTypeDialog; 